import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  Mail,
  Send,
  ChevronDown,
  Code,
  Cloud,
  ShieldCheck,
  TrendingUp,
  Headphones,
} from "lucide-react";

import Logo from "../assets/Logo1.png";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const location = useLocation();

  // Route மாறும்போது page மேலிருந்து (Top) Scroll ஆவதற்கான Effect
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Smooth-ஆ Scroll ஆக வேண்டாமென்றால் "auto" எனக் கொடுக்கலாம்
    });
  }, [location.pathname]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    phone: "",
    message: "",
  });

  // Services submenu items
  const servicesSubMenu = [
    {
      title: "Software Development",
      desc: "Custom software solutions designed to meet your unique business needs.",
      icon: Code,
      path: "/services/software-development",
    },
    {
      title: "Cloud Solutions",
      desc: "Scalable and secure cloud services to accelerate your digital transformation.",
      icon: Cloud,
      path: "/services/cloud-solutions",
    },
    {
      title: "Cyber Security",
      desc: "Protect your business with advanced security solutions and best practices.",
      icon: ShieldCheck,
      path: "/services/cyber-security",
    },
    {
      title: "IT Consulting",
      desc: "Expert guidance to optimize your IT strategy and drive business growth.",
      icon: TrendingUp,
      path: "/services/it-consulting",
    },
    {
      title: "Managed IT Services",
      desc: "End-to-end IT support and management to keep your business running smoothly.",
      icon: Headphones,
      path: "/services/managed-it-services",
    },
  ];

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services", hasSubmenu: true },
    { name: "Industries", path: "/industries" },
    { name: "Case Studies", path: "/case-studies" },
    { name: "Contact", path: "/contact" },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    setTimeout(() => {
      setIsSubmitted(false);
      setIsModalOpen(false);
      setFormData({
        name: "",
        email: "",
        service: "",
        phone: "",
        message: "",
      });
    }, 2500);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex gap-5">
            <a href="tel:+91956663558" className="flex items-center gap-2 hover:text-[#f25c05] transition">
              <Phone size={14} className="text-[#f25c05]" />
              +91 95666 63558
            </a>

            <a href="mailto:contactus@inim-ai.com" className="flex items-center gap-2 hover:text-[#f25c05] transition">
              <Mail size={14} className="text-[#f25c05]" />
              contactus@inim-ai.com
            </a>
          </div>
        </div>
      </div>

      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
          {/* Logo */}
          <NavLink to="/" onClick={closeMobileMenu}>
            <img src={Logo} alt="Logo" className="h-16 " />
          </NavLink>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.hasSubmenu) {
                return (
                  <div key={link.path} className="relative group py-6">
                    <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                        `flex items-center gap-1 text-[15px] font-semibold transition duration-300 ${
                          isActive
                            ? "text-[#f25c05]"
                            : "text-slate-700 hover:text-[#f25c05]"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span>{link.name}</span>
                          <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
                          {isActive && (
                            <span className="absolute left-0 bottom-4 h-[3px] w-full rounded-full bg-[#f25c05]" />
                          )}
                        </>
                      )}
                    </NavLink>

                    {/* Desktop Submenu Dropdown */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-[600px] bg-white shadow-xl rounded-xl border border-slate-100 p-4 grid grid-cols-2 gap-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                      {servicesSubMenu.map((item) => {
                        const IconComponent = item.icon;
                        return (
                          <NavLink
                            key={item.title}
                            to={item.path}
                            className={({ isActive }) =>
                              `p-3 rounded-lg flex items-start gap-3 transition ${
                                isActive
                                  ? "bg-orange-50 border-l-4 border-[#f25c05]"
                                  : "hover:bg-slate-50"
                              }`
                            }
                          >
                            <div className="p-2 bg-orange-100 text-[#f25c05] rounded-md mt-0.5">
                              <IconComponent size={18} />
                            </div>
                            <div>
                              <div className="font-semibold text-slate-800 text-sm hover:text-[#f25c05]">
                                {item.title}
                              </div>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                                {item.desc}
                              </p>
                            </div>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) =>
                    `relative text-[15px] font-semibold transition duration-300 ${
                      isActive
                        ? "text-[#f25c05]"
                        : "text-slate-700 hover:text-[#f25c05]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {link.name}
                      {isActive && (
                        <span className="absolute left-0 -bottom-2 h-[3px] w-full rounded-full bg-[#f25c05]" />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Desktop Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="hidden lg:block bg-[#f25c05] hover:bg-[#d95000] text-white px-6 py-2 rounded-lg font-semibold transition shadow-md hover:shadow-lg"
          >
            Get In Touch
          </button>

          {/* Mobile Menu Toggle Button */}
          <button
            className="lg:hidden text-slate-800 p-2 focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 max-h-[80vh] overflow-y-auto shadow-inner">
            {navLinks.map((link) => {
              if (link.hasSubmenu) {
                return (
                  <div key={link.path} className="border-b border-slate-100">
                    <div className="flex justify-between items-center px-6 py-4">
                      <NavLink
                        to={link.path}
                        onClick={closeMobileMenu}
                        className={({ isActive }) =>
                          `font-bold text-base transition ${
                            isActive ? "text-[#f25c05]" : "text-slate-800"
                          }`
                        }
                      >
                        {link.name}
                      </NavLink>
                      <button
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className="p-1 text-slate-500 hover:text-[#f25c05]"
                      >
                        <ChevronDown
                          size={20}
                          className={`transition-transform duration-300 ${
                            mobileServicesOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {/* Mobile Services Accordion */}
                    {mobileServicesOpen && (
                      <div className="bg-slate-50 px-4 py-2 space-y-1">
                        {servicesSubMenu.map((subItem) => {
                          const IconComponent = subItem.icon;
                          return (
                            <NavLink
                              key={subItem.title}
                              to={subItem.path}
                              onClick={closeMobileMenu}
                              className={({ isActive }) =>
                                `flex items-center gap-3 p-3 rounded-lg text-sm font-medium transition ${
                                  isActive
                                    ? "bg-[#f25c05] text-white font-semibold shadow-sm"
                                    : "text-slate-700 hover:bg-orange-100/50 hover:text-[#f25c05]"
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  <IconComponent
                                    size={18}
                                    className={isActive ? "text-white" : "text-[#f25c05]"}
                                  />
                                  <span>{subItem.title}</span>
                                </>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  onClick={closeMobileMenu}
                  className={({ isActive }) =>
                    `block px-6 py-4 font-semibold text-base border-b border-slate-100 transition ${
                      isActive
                        ? "bg-orange-50 text-[#f25c05] border-l-4 border-[#f25c05]"
                        : "text-slate-700 hover:bg-orange-50/50 hover:text-[#f25c05]"
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              );
            })}

            {/* Mobile Contact Action Button */}
            <div className="p-5">
              <button
                onClick={() => {
                  closeMobileMenu();
                  setIsModalOpen(true);
                }}
                className="w-full bg-[#f25c05] active:bg-[#d95000] text-white py-3 rounded-lg font-bold shadow-md"
              >
                Get In Touch
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Contact Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 relative shadow-2xl">
            <button
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
              onClick={() => setIsModalOpen(false)}
            >
              <X size={20} />
            </button>

            {isSubmitted ? (
              <div className="text-center py-10">
                <Send className="mx-auto text-green-600 mb-3" size={40} />
                <h2 className="text-xl font-bold text-slate-800">
                  Message Sent Successfully!
                </h2>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <h3 className="text-xl font-bold text-slate-800 mb-2">Get In Touch</h3>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full Name"
                  className="w-full border border-slate-200 p-3 rounded-lg focus:outline-none focus:border-[#f25c05]"
                  required
                />

                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className="w-full border border-slate-200 p-3 rounded-lg focus:outline-none focus:border-[#f25c05]"
                  required
                />

                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone"
                  className="w-full border border-slate-200 p-3 rounded-lg focus:outline-none focus:border-[#f25c05]"
                  required
                />

                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full border border-slate-200 p-3 rounded-lg focus:outline-none focus:border-[#f25c05]"
                  required
                >
                  <option value="">Select Service</option>
                  {servicesSubMenu.map((item) => (
                    <option key={item.title} value={item.title}>
                      {item.title}
                    </option>
                  ))}
                </select>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Message"
                  className="w-full border border-slate-200 p-3 rounded-lg focus:outline-none focus:border-[#f25c05]"
                  required
                />

                <button className="w-full bg-[#f25c05] hover:bg-[#d95000] text-white py-3 rounded-lg font-semibold transition">
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}